import Swal, { SweetAlertIcon, SweetAlertOptions } from 'sweetalert2';
import { toastSuccess } from '@components/common/Toast/CustomToast';

// 기본 설정
const defaultOptions = {
    confirmButtonColor: '#3085d6',
    cancelButtonColor: '#d33',
    confirmButtonText: '확인',
    cancelButtonText: '취소',
    customClass: {
        popup: 'custom-swal-popup',
        title: 'custom-swal-title',
        htmlContainer: 'custom-swal-content',
    },
} as const;

// Alert 유틸리티 클래스
export class Alert {
    // 성공 알림
    static success(title: string, text?: string, options?: SweetAlertOptions) {
        return Swal.fire({
            ...defaultOptions,
            icon: 'success',
            title,
            text,
            confirmButtonText: '확인',
            ...options,
        });
    }

    // 에러 알림
    static error(title: string, text?: string, options?: SweetAlertOptions) {
        return Swal.fire({
            ...defaultOptions,
            icon: 'error',
            title,
            text,
            confirmButtonText: '확인',
            ...options,
        });
    }

    // 경고 알림
    static warning(title: string, text?: string, options?: SweetAlertOptions) {
        return Swal.fire({
            ...defaultOptions,
            icon: 'warning',
            title,
            text,
            confirmButtonText: '확인',
            ...options,
        });
    }

    // 정보 알림
    static info(title: string, text?: string, options?: SweetAlertOptions) {
        return Swal.fire({
            ...defaultOptions,
            icon: 'info',
            title,
            text,
            confirmButtonText: '확인',
            ...options,
        });
    }

    // 확인 다이얼로그
    static confirm(
        title: string,
        text?: string,
        confirmText: string = '확인',
        cancelText: string = '취소',
        options?: SweetAlertOptions,
        showCancelButton?: boolean,
        iconName?: SweetAlertIcon,
    ) {
        return Swal.fire({
            ...defaultOptions,
            icon: iconName || 'question',
            title,
            text,
            showCancelButton: showCancelButton,
            confirmButtonText: confirmText,
            cancelButtonText: cancelText,
            ...options,
        });
    }

    // 삭제 확인 다이얼로그
    static delete(
        title: string = '정말 삭제하시겠습니까?',
        text?: string,
        options?: SweetAlertOptions,
    ) {
        return Swal.fire({
            ...defaultOptions,
            icon: 'warning',
            title,
            text,
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: '삭제',
            cancelButtonText: '취소',
            ...options,
        });
    }

    // 입력 다이얼로그
    static input(
        title: string,
        inputType:
            | 'text'
            | 'email'
            | 'password'
            | 'number'
            | 'tel'
            | 'url'
            | 'textarea'
            | 'select'
            | 'radio'
            | 'checkbox'
            | 'file'
            | 'range' = 'text',
        placeholder?: string,
        options?: SweetAlertOptions,
    ) {
        const isFileInput = inputType === 'file';

        return Swal.fire({
            ...defaultOptions,
            title,
            input: inputType,
            inputPlaceholder: placeholder,
            showCancelButton: true,
            confirmButtonText: '확인',
            cancelButtonText: '취소',
            preConfirm: (value: string | File | FileList | null) => {
                if (isFileInput) {
                    const file = value as File | FileList | null;
                    if (!file) {
                        Swal.showValidationMessage('파일을 선택해주세요!');
                    }
                    return file;
                } else {
                    if (!value || typeof value !== 'string') {
                        Swal.showValidationMessage('입력값이 필요합니다!');
                    }
                    return value;
                }
            },
            ...options,
        } as SweetAlertOptions); // 타입 좁히기가 완전히 안 되는 경우엔 여전히 필요
    }

    // 로딩 다이얼로그
    static loading(title: string = '처리 중...', text?: string) {
        return Swal.fire({
            ...defaultOptions,
            title,
            text,
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
                Swal.showLoading();
            },
        });
    }

    // 토스트 알림
    static toast(
        title: string,
        icon: 'success' | 'error' | 'warning' | 'info' | 'question' = 'info',
        position:
            | 'top'
            | 'top-start'
            | 'top-end'
            | 'center'
            | 'center-start'
            | 'center-end'
            | 'bottom'
            | 'bottom-start'
            | 'bottom-end' = 'top-end',
        timer: number = 3000,
    ) {
        const Toast = Swal.mixin({
            toast: true,
            position,
            showConfirmButton: false,
            timer,
            timerProgressBar: true,
            didOpen: toast => {
                toast.onmouseenter = Swal.stopTimer;
                toast.onmouseleave = Swal.resumeTimer;
            },
        });

        return Toast.fire({
            icon,
            title,
        });
    }

    // 커스텀 알림
    static custom(options: SweetAlertOptions) {
        return Swal.fire({
            ...defaultOptions,
            ...options,
        });
    }

    // 다중 입력 양식 알림
    static multiInput(title: string, inputs: { [key: string]: any }) {
        return Swal.fire({
            title,
            html: Object.keys(inputs)
                .map(
                    key =>
                        `<input id="${key}" class="swal2-input" placeholder="${inputs[key].placeholder || key}" type="${inputs[key].type || 'text'}">`,
                )
                .join(''),
            confirmButtonText: '확인',
            showCancelButton: true,
            cancelButtonText: '취소',
            focusConfirm: false,
            preConfirm: () => {
                const result: { [key: string]: any } = {};
                Object.keys(inputs).forEach(key => {
                    const input = document.getElementById(
                        key,
                    ) as HTMLInputElement;
                    result[key] = input.value;
                });
                return result;
            },
        });
    }

    // 단계별 알림 (Queue)
    // static queue(steps: { title: string; text?: string; input?: string }[]) {
    //     const Queue = Swal.mixin({
    //         progressSteps: steps.map((_, index) => `${index + 1}`),
    //         showCancelButton: true,
    //         confirmButtonText: '다음',
    //         cancelButtonText: '취소',
    //     });
    //
    //     return steps.reduce((promise, step, index) => {
    //         return promise.then(async previousResults => {
    //             const result = await Queue.fire({
    //                 title: step.title,
    //                 text: step.text,
    //                 input: step.input,
    //                 currentProgressStep: index,
    //                 confirmButtonText:
    //                     index === steps.length - 1 ? '완료' : '다음',
    //             });
    //
    //             if (result.value) {
    //                 return [...(previousResults || []), result.value];
    //             }
    //             throw new Error('Cancelled');
    //         });
    //     }, Promise.resolve([]));
    // }

    // 알림 닫기
    static close() {
        Swal.close();
    }

    // 현재 알림이 열려있는지 확인
    static isVisible() {
        return Swal.isVisible();
    }

    // 로딩 상태 표시/숨김
    static showLoading() {
        Swal.showLoading();
    }

    static hideLoading() {
        Swal.hideLoading();
    }

    // 검증 메시지 표시
    static showValidationMessage(message: string) {
        Swal.showValidationMessage(message);
    }

    // 결과 처리 유틸리티
    static handleResult(
        result: any,
        onConfirm?: (value: any) => void,
        onCancel?: () => void,
    ) {
        if (result.isConfirmed && onConfirm) {
            onConfirm(result.value);
        } else if (result.isDismissed && onCancel) {
            onCancel();
        }
    }

    static confirmAndExecute(
        title: string,
        text: string,
        showSuccessToast?: boolean,
        onConfirm?: () => void | Promise<void>,
        onCancel?: () => void,
        options?: SweetAlertOptions,
        showCancelButton?: boolean,
    ) {
        return this.confirm(
            title,
            text,
            '확인',
            '취소',
            options,
            showCancelButton || true,
        ).then(async result => {
            if (result.isConfirmed) {
                if (onConfirm) {
                    await onConfirm();
                    if (showSuccessToast) {
                        toastSuccess('처리되었습니다.');
                    }
                }
            } else if (result.isDismissed && onCancel) {
                onCancel();
            }
        });
    }

    static alert(
        title: string,
        text: string,
        onConfirm?: () => void | Promise<void>,
    ) {
        return this.confirm(
            title,
            text,
            '확인',
            '취소',
            undefined,
            false,
            'warning',
        ).then(async result => {
            if (result.isConfirmed) {
                if (onConfirm) {
                    await onConfirm();
                }
            }
        });
    }

    static confirmWithPassword(
        title: string,
        text: string,
        password: string, // 비교할 고정 비밀번호
        showSuccessToast?: boolean,
        onConfirm?: () => void | Promise<void>,
        onCancel?: () => void,
        options?: SweetAlertOptions,
    ) {
        return Swal.fire({
            ...defaultOptions,
            icon: 'warning',
            title,
            text,
            input: 'password',
            inputPlaceholder: '비밀번호를 입력해주세요',
            inputAttributes: {
                autocapitalize: 'off',
                autocorrect: 'off',
            },
            showCancelButton: true,
            confirmButtonText: '확인',
            cancelButtonText: '취소',
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            preConfirm: inputValue => {
                if (!inputValue) {
                    Swal.showValidationMessage('비밀번호를 입력해주세요!');
                    return false;
                }
                if (inputValue !== password) {
                    Swal.showValidationMessage('비밀번호가 일치하지 않습니다!');
                    return false;
                }
                return inputValue;
            },
            ...options,
        }).then(async result => {
            if (result.isConfirmed) {
                if (onConfirm) {
                    await onConfirm();
                    if (showSuccessToast) {
                        toastSuccess('처리되었습니다.');
                    }
                }
            } else if (result.isDismissed && onCancel) {
                onCancel();
            }
        });
    }
}
